import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import './index.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Reusable Histogram component
function Histogram({ labels, data, title, xAxisTitle, yAxisTitle }) {
  const chartData = {
    labels: labels,
    datasets: [
      {
        label: yAxisTitle || 'Count',
        data: data,
        backgroundColor: '#1976d2',
        borderColor: '#1565c0',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { title: { display: true, text: xAxisTitle || 'Category' } },
      y: { title: { display: true, text: yAxisTitle || 'Count' } },
    },
    plugins: {
      legend: { display: false },
      title: { display: true, text: title },
    },
  };

  return (
    <div style={{ height: '300px' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}

function MarketTrends() {
  // States
  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [combinedHistogram, setCombinedHistogram] = useState({});
  const [jobCountsByCategory, setJobCountsByCategory] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingHistogram, setLoadingHistogram] = useState(true);
  const [loadingCategoryCounts, setLoadingCategoryCounts] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [chartType, setChartType] = useState('salary');

  // Resume & Cover Letter States
  const [uploadedResume, setUploadedResume] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState({});
  const [generatedCoverLetters, setGeneratedCoverLetters] = useState({});
  const [showCoverLetter, setShowCoverLetter] = useState({});
  const [selectedAIProvider, setSelectedAIProvider] = useState('local');
  const fileInputRef = useRef(null);

  // BACKEND URL - CHANGE THIS WHEN DEPLOYING!
  // const BACKEND_URL = "http://localhost:5000";  // Change to your deployed backend URL later

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // AI Provider Status (Gemini availability is now determined by backend)
  const AI_PROVIDERS = {
    gemini: { name: 'Google Gemini', icon: 'AI' },
    local: { name: 'Local Template', icon: 'Document' },
  };

  useEffect(() => {
    const status = {};
    Object.keys(AI_PROVIDERS).forEach(p => {
      status[p] = { available: true, ...AI_PROVIDERS[p] };
    });
    setAiProviderStatus(status);
  }, []);

  const [aiProviderStatus, setAiProviderStatus] = useState({});

  // 1. Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      setError(null);
      try {
        const res = await fetch(`${BACKEND_URL}/api/categories`);
        if (!res.ok) throw new Error("Failed to fetch categories");
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [BACKEND_URL]);

  // 2. Fetch Jobs
  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true);
      setError(null);
      try {
        const res = await fetch(`${BACKEND_URL}/api/jobs?filter=${filter}`);
        if (!res.ok) throw new Error("Failed to fetch jobs");
        const data = await res.json();
        setJobs(data);
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, [filter, BACKEND_URL]);

  // 3. Fetch Combined Histogram
  useEffect(() => {
    if (categories.length === 0) return;

    const fetchHistogram = async () => {
      setLoadingHistogram(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/combined-histogram`);
        if (!res.ok) throw new Error("Failed to fetch histogram");
        const data = await res.json();
        setCombinedHistogram(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingHistogram(false);
      }
    };
    fetchHistogram();
  }, [categories, BACKEND_URL]);

  // 4. Fetch Category Counts
  useEffect(() => {
    if (categories.length === 0) return;

    const fetchCounts = async () => {
      setLoadingCategoryCounts(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/category-counts`);
        if (!res.ok) throw new Error("Failed to fetch category counts");
        const data = await res.json();
        setJobCountsByCategory(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingCategoryCounts(false);
      }
    };
    fetchCounts();
  }, [categories, BACKEND_URL]);

  // Handle Resume Upload (mock text extraction)
  const handleResumeUpload = (event) => {
    const file = event.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      alert('Please upload a PDF file only.');
      return;
    }
    setUploadedResume(file);
    const mockResumeText = `Professional Resume - ${file.name}
Experience: Software Developer with expertise in React, JavaScript, Node.js, and Python.
Skills: Full-stack development, database management, API integration, and team collaboration.
Education: Computer Science degree with strong foundation in algorithms and software engineering.
Projects: Built multiple web applications using modern frameworks and cloud technologies.`;
    setResumeText(mockResumeText);
  };

  // Generate Cover Letter via Backend
  const generateCoverLetter = async (job) => {
    if (!uploadedResume) {
      alert('Please upload your resume first!');
      return;
    }

    const jobId = job.id;
    setGeneratingCoverLetter(prev => ({ ...prev, [jobId]: true }));

    try {
      const response = await fetch(`${BACKEND_URL}/api/generate-cover-letter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job: job,
          resumeText: resumeText,
          provider: selectedAIProvider  // "local" or "gemini"
        })
      });

      if (!response.ok) throw new Error("Failed to generate cover letter");

      const result = await response.json();
      setGeneratedCoverLetters(prev => ({ ...prev, [jobId]: result.coverLetter }));
      setShowCoverLetter(prev => ({ ...prev, [jobId]: true }));
    } catch (err) {
      console.error(err);
      alert("Failed to generate. Using local template as fallback.");
      const fallback = `Dear Hiring Manager,\n\nI am writing to express my interest in the ${job.title} position...`;
      setGeneratedCoverLetters(prev => ({ ...prev, [jobId]: fallback }));
      setShowCoverLetter(prev => ({ ...prev, [jobId]: true }));
    } finally {
      setGeneratingCoverLetter(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Copy & Download (unchanged)
  const copyCoverLetter = (text) => {
    navigator.clipboard.writeText(text).then(() => alert('Copied!')).catch(() => alert('Copy failed'));
  };

  const downloadCoverLetter = (text, jobTitle, company) => {
    const fileName = `Cover_Letter_${jobTitle.replace(/\s+/g, '_')}_${company?.replace(/\s+/g, '_') || 'Job'}.txt`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  };

  const formatSalary = (min, max) => {
    if (min && max) return `₹${min.toLocaleString()} - ₹${max.toLocaleString()}`;
    if (min) return `₹${min.toLocaleString()}+`;
    if (max) return `Up to ₹${max.toLocaleString()}`;
    return 'Salary not specified';
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ color: '#2c3e50', textAlign: 'center', marginBottom: '10px' }}>Job Market Trends Dashboard</h1>
      <p style={{ textAlign: 'center', color: '#6c757d', marginBottom: '30px' }}>
        Explore job opportunities and market trends in the Indian job market.
      </p>

      {/* Resume Upload */}
      <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f1f8ff', borderRadius: '8px', border: '2px solid #b6d7ff' }}>
        <h3 style={{ color: '#0366d6', marginBottom: '15px' }}>Upload Resume for AI Cover Letter</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <input type="file" ref={fileInputRef} onChange={handleResumeUpload} accept=".pdf" style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()} style={{
              backgroundColor: '#0366d6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px',
              fontWeight: '600', cursor: 'pointer'
            }}>
              Upload Resume (PDF)
            </button>
            {uploadedResume && <span style={{ color: '#28a745', fontWeight: '600' }}>✅ {uploadedResume.name}</span>}
          </div>

          <div>
            <label style={{ fontWeight: '600' }}>AI Provider:</label>
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              {Object.keys(AI_PROVIDERS).map(p => (
                <button
                  key={p}
                  onClick={() => setSelectedAIProvider(p)}
                  style={{
                    backgroundColor: selectedAIProvider === p ? '#28a745' : '#6c757d',
                    color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px',
                    fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  {AI_PROVIDERS[p].icon} {AI_PROVIDERS[p].name} {selectedAIProvider === p && '✓'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50' }}>Job Opportunities</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button onClick={() => setFilter('all')} style={{ backgroundColor: filter === 'all' ? '#007bff' : '#6c757d', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '6px' }}>All Jobs</button>
          <button onClick={() => setFilter('software')} style={{ backgroundColor: filter === 'software' ? '#007bff' : '#6c757d', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '6px' }}>Software Jobs</button>
        </div>

        {error && <div style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>{error}</div>}

        {loadingJobs ? <p>Loading jobs...</p> : jobs.length === 0 ? <p>No jobs found.</p> : (
          <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
            {jobs.map(job => (
              <div key={job.id} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <h5 style={{ color: '#007bff', margin: '0 0 8px' }}>{job.title}</h5>
                    <p style={{ margin: '0 0 5px', color: '#28a745', fontWeight: '600' }}>Company: {job.company?.display_name}</p>
                    <p style={{ margin: '0 0 5px', color: '#6c757d' }}>Location: {job.location?.display_name}</p>
                    <p style={{ color: '#17a2b8', fontWeight: '600' }}>Salary: {formatSalary(job.salary_min, job.salary_max)}</p>
                  </div>
                </div>
                <p style={{ color: '#6c757d', margin: '15px 0' }}>{job.description_clean || job.description}</p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => generateCoverLetter(job)}
                    disabled={generatingCoverLetter[job.id] || !uploadedResume}
                    style={{ backgroundColor: generatingCoverLetter[job.id] ? '#ffc107' : '#28a745', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '4px' }}
                  >
                    {generatingCoverLetter[job.id] ? 'Generating...' : 'Generate Cover Letter'}
                  </button>
                  {generatedCoverLetters[job.id] && (
                    <>
                      <button onClick={() => setShowCoverLetter(prev => ({ ...prev, [job.id]: !prev[job.id] }))} style={{ backgroundColor: '#17a2b8', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '4px' }}>
                        {showCoverLetter[job.id] ? 'Hide' : 'View'} Letter
                      </button>
                      <button onClick={() => copyCoverLetter(generatedCoverLetters[job.id])} style={{ backgroundColor: '#6f42c1', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '4px' }}>Copy</button>
                      <button onClick={() => downloadCoverLetter(generatedCoverLetters[job.id], job.title, job.company?.display_name)} style={{ backgroundColor: '#fd7e14', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '4px' }}>Download</button>
                    </>
                  )}
                </div>
                {showCoverLetter[job.id] && (
                  <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, color: '#495057' }}>
                      {generatedCoverLetters[job.id]}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts */}
      <div style={{ padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', marginBottom: '30px' }}>
        <h3 style={{ color: '#856404' }}>Market Trends</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button onClick={() => setChartType('salary')} style={{ backgroundColor: chartType === 'salary' ? '#007bff' : '#6c757d', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '6px' }}>Salary Distribution</button>
          <button onClick={() => setChartType('category')} style={{ backgroundColor: chartType === 'category' ? '#007bff' : '#6c757d', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '6px' }}>Jobs by Category</button>
        </div>

        {chartType === 'salary' ? (
          loadingHistogram ? <p>Loading salary data...</p> :
          Object.keys(combinedHistogram).length > 0 ? (
            <Histogram
              labels={Object.keys(combinedHistogram).map(k => {
                const s = parseInt(k);
                return s === 0 ? '0-10L' : `${(s / 100000).toFixed(0)}L-${((s + 100000) / 100000).toFixed(0)}L`;
              })}
              data={Object.values(combinedHistogram)}
              title="Salary Distribution (₹)"
              xAxisTitle="Salary Range"
              yAxisTitle="Number of Jobs"
            />
          ) : <p>No salary data</p>
        ) : (
          loadingCategoryCounts ? <p>Loading...</p> :
          Object.keys(jobCountsByCategory).length > 0 ? (
            <Histogram
              labels={Object.keys(jobCountsByCategory)}
              data={Object.values(jobCountsByCategory)}
              title="Jobs by Category"
              xAxisTitle="Category"
              yAxisTitle="Job Count"
            />
          ) : <p>No category data</p>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', color: '#6c757d' }}>
        <p>Job Market Trends Dashboard • Powered by Secure Backend + AI</p>
      </div>
    </div>
  );
}

export default MarketTrends;